import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { createStore, useAtomValue, useSetAtom } from 'jotai';
import type { MapRef } from 'react-map-gl';
import type { Geometry } from 'geojson';
import { api } from 'lib/request/api';
import type {
  AreaGeometry,
  AreaSubscription,
  SubscriptionTopic,
} from 'types/area-geometry';
import {
  apiPath,
  readWriteFailure,
  toWorkspaceError,
} from 'pages/area-workspace/utils';
import {
  allDatasetAtoms,
  lastSpatialFilterAtom,
  mapSearchRefAtom,
  spatialFilterModeAtom,
} from 'pages/map-query/atoms';
import { datasetRegistry } from 'pages/map-query/datasets';
import type { DatasetSearchParams } from 'pages/map-query/types';
import {
  crossSectionArmedAtom,
  disarmCrossSectionAtom,
  ELIGIBLE_MIN_ZOOM,
  hostWellColorsAtom,
  sectionModeAtom,
  sectionModeMenuOpenAtom,
  sectionVerticesAtom,
  showWellResultsAtom,
  startSectionAtom,
  wellColorRulesAtom,
  wellResultsRequestedAtom,
} from 'components/cross-section';
import type { ColorByRule } from 'components/color-by-attribute-modal';
import { buildColorByUwiColorMap } from 'pages/pre-drill-wellbore-workspace/utils';
import type { FilteredUwi } from 'types/pre-drill-workspace';
import { CURRENT_AREA_QUERY_KEY } from './use-current-area';
import {
  DEFAULT_SECTION_VISIBILITY,
  type SectionKey,
  type SectionVisibility,
} from './section-visibility';
import { calculateGeometryBounds } from './utils';
import {
  areaColorByUwiMapAtom,
  selectedWellUwiAtom,
  wellStatusColorsAtom,
} from './atoms';
import { areaWellColorMap } from './color-by';
import { WELL_STATUSES, WELL_STATUS_COLORS } from './well-statuses';

export interface AreaSubscriptionWrites {
  busy: boolean;
  error: string | null;
  setTopics: (topics: SubscriptionTopic[]) => void;
  clearError: () => void;
}

/**
 * Saves the viewer's subscription topics on an area. One POST sets the full list, and
 * `[]` unsubscribes.
 *
 * @param {string | undefined} routeId - The detail route param the area is cached under.
 * @param {AreaSubscription | undefined} subscription - The area's subscription block.
 * @return {AreaSubscriptionWrites} Whether a save is in flight, why it failed, the setter, and a way to clear the failure.
 */
export const useAreaSubscription = (
  routeId: string | undefined,
  subscription: AreaSubscription | undefined
): AreaSubscriptionWrites => {
  const queryClient = useQueryClient();

  const areaKey = [CURRENT_AREA_QUERY_KEY, routeId];

  const { mutate, isPending, error, reset } = useMutation<
    void,
    Error,
    SubscriptionTopic[]
  >({
    mutationFn: async (topics) => {
      if (!subscription) return;
      try {
        await api.post(apiPath(subscription.href), {
          json: {
            subscriptions: [
              {
                subscribableType: subscription.subscribableType,
                subscribableId: subscription.subscribableId,
                topics,
              },
            ],
          },
        });
      } catch (requestError) {
        throw await toWorkspaceError(requestError);
      }
    },
    retry: false,
    onSuccess: async (_, topics) => {
      // A refetch that started before the save would land after it with the old topics.
      await queryClient.cancelQueries({ queryKey: areaKey });
      queryClient.setQueryData<AreaGeometry>(areaKey, (area) =>
        area?.subscription
          ? {
              ...area,
              subscription: {
                ...area.subscription,
                topics,
                subscribed: topics.length > 0,
              },
            }
          : area
      );
    },
  });

  return {
    busy: isPending,
    error: readWriteFailure(
      error,
      'Could not save your subscription settings.'
    ),
    setTopics: (topics) => mutate(topics),
    clearError: reset,
  };
};

/** The Cross Section section's state, and the toggle for every section. */
export interface CrossSectionSection {
  /** The tool is armed. */
  armed: boolean;
  /** The box is ticked: the card shows. */
  on: boolean;
  /** Toggles any section, Cross Section through the tool. */
  toggle: (section: SectionKey) => void;
}

/**
 * UAT: the line a section opens on — west to east through the middle of the area's
 * extent. Close to a bisection, not exact; the reader drags it from there.
 *
 * @param {Geometry | null | undefined} geometry - The area's boundary.
 * @return {Array | null} The line's two ends, or null without a boundary.
 */
export const presetSectionLine = (
  geometry: Geometry | null | undefined
): { lat: number; lon: number }[] | null => {
  const bounds = calculateGeometryBounds(geometry);
  if (!bounds) {
    return null;
  }
  const [[west, north], [east, south]] = bounds;
  const lat = (north + south) / 2;
  return [
    { lat, lon: west },
    { lat, lon: east },
  ];
};

/**
 * Story 7: the Cross Section section, shown by its box like the others. While ticked
 * the tool stays armed, so the card stays: the map's own button asks for a method
 * again rather than taking the card away. Hiding the Map unticks it, as the tool needs
 * a map to draw on. Leaving the page puts the tool away.
 *
 * UAT: on as the page opens, and on ticking, it opens on a preset Arbitrary Line
 * across the area with the well intersections already asked for. Clear keeps it on,
 * so after Clear the reader chooses and draws their own.
 *
 * @param {boolean} mapShown - Whether the Map section is shown.
 * @param {Dispatch} setVisibility - The page's section setter.
 * @param {Function} toggleOther - Toggles any other section.
 * @param {Geometry | null | undefined} geometry - The area's boundary.
 * @return {CrossSectionSection} The section's state and toggle.
 */
export function useCrossSectionSection(
  mapShown: boolean,
  setVisibility: Dispatch<SetStateAction<SectionVisibility>>,
  toggleOther: (section: SectionKey) => void,
  geometry: Geometry | null | undefined
): CrossSectionSection {
  const armed = useAtomValue(crossSectionArmedAtom);
  const menuOpen = useAtomValue(sectionModeMenuOpenAtom);
  const setArmed = useSetAtom(crossSectionArmedAtom);
  const setMenuOpen = useSetAtom(sectionModeMenuOpenAtom);
  const disarm = useSetAtom(disarmCrossSectionAtom);
  const startSection = useSetAtom(startSectionAtom);
  const setVertices = useSetAtom(sectionVerticesAtom);
  const setWellsRequested = useSetAtom(wellResultsRequestedAtom);
  const setShowWells = useSetAtom(showWellResultsAtom);
  const [on, setOn] = useState(DEFAULT_SECTION_VISIBILITY.crossSection);
  const presetLine = useMemo(() => presetSectionLine(geometry), [geometry]);

  // Without a boundary there is nothing to preset, so it asks for a method instead.
  const turnOn = useCallback(() => {
    setVisibility((current) => ({ ...current, map: true }));
    setArmed(true);
    if (!presetLine) {
      setMenuOpen(true);
      return;
    }
    startSection('arbitrary_line');
    setVertices(presetLine);
    setWellsRequested(true);
    setShowWells(true);
  }, [
    presetLine,
    setVisibility,
    setArmed,
    setMenuOpen,
    startSection,
    setVertices,
    setWellsRequested,
    setShowWells,
  ]);

  const toggle = useCallback(
    (section: SectionKey) => {
      if (section === 'map' && mapShown) {
        setOn(false);
      }
      if (section !== 'crossSection') {
        toggleOther(section);
        return;
      }
      setOn(!on);
      if (on) {
        disarm();
        return;
      }
      turnOn();
    },
    [on, mapShown, disarm, turnOn, toggleOther]
  );

  // Whether the method prompt was open when the map's button put the tool away.
  const menuWasOpen = useRef(false);
  useEffect(() => {
    if (armed) {
      menuWasOpen.current = menuOpen;
    }
  }, [armed, menuOpen]);

  // While ticked the tool stays armed: on as the page opens, and the map's button
  // re-asks for a method instead of taking the card away.
  const opened = useRef(false);
  useEffect(() => {
    const opening = !opened.current;
    opened.current = true;
    if (!on || armed) {
      return;
    }
    if (opening) {
      turnOn();
      return;
    }
    setArmed(true);
    setMenuOpen(!menuWasOpen.current);
  }, [on, armed, turnOn, setArmed, setMenuOpen]);

  useEffect(
    () => () => {
      disarm();
    },
    [disarm]
  );

  return { armed, on, toggle };
}

/**
 * UAT: the section's wells take the rail's colours — the legend's status colours with
 * any colour rule over them, as the map paints them — while the section is up. Wells
 * outside the area keep the section's own colour. Cleared when the section goes, as
 * the tool's state is shared with every map that offers it.
 *
 * @param {ColorByRule[]} colorRules - The rail's colour rules.
 * @param {FilteredUwi[]} wells - The area's wells, each with its status.
 */
export function useCrossSectionWellColors(
  colorRules: ColorByRule[],
  wells: FilteredUwi[]
): void {
  const armed = useAtomValue(crossSectionArmedAtom);
  const colorByUwiMap = useAtomValue(areaColorByUwiMapAtom);
  const statusColors = useAtomValue(wellStatusColorsAtom);
  const setHostWellColors = useSetAtom(hostWellColorsAtom);

  const wellColors = useMemo(
    () =>
      areaWellColorMap({
        wells,
        statusColors,
        ruleColors: buildColorByUwiColorMap(colorRules, colorByUwiMap),
      }),
    [wells, statusColors, colorRules, colorByUwiMap]
  );

  useEffect(() => {
    if (!armed) {
      return undefined;
    }
    setHostWellColors(wellColors);
    return () => setHostWellColors({});
  }, [armed, wellColors, setHostWellColors]);

  // Every other crossing — the line runs past the boundary — by status in the
  // legend's colours, as the Main Map's section colours all of them. Section only:
  // the map's wells are the legend's already.
  const setWellRules = useSetAtom(wellColorRulesAtom);
  useEffect(() => {
    if (!armed) {
      return undefined;
    }
    const valueColors = { ...WELL_STATUS_COLORS, ...statusColors };
    setWellRules([
      {
        ruleId: 'area-well-color-by-status',
        attribute: 'status',
        selectedValues: WELL_STATUSES.map((status) => status.value),
        selectedValueLabels: WELL_STATUSES.map((status) => status.label),
        valueColors,
        // `map: false` spelled out: the map's well paint treats a missing flag as on.
        applyTo: { crossSection: true, map: false },
        enabled: true,
      },
    ]);
    return () => setWellRules([]);
  }, [armed, statusColors, setWellRules]);
}

/**
 * UAT: Well to Well only offers vertical wells from zoom 12, and this map opens
 * further out than that, so choosing it showed nothing to pick. The page says so
 * rather than moving the map; picking it redraws so the section recounts what is in
 * view.
 *
 * @param {MapRef | null} mapRef - The area's map.
 * @return {boolean} Well to Well is on and the map is too far out to offer wells.
 */
export function useWellToWellZoom(mapRef: MapRef | null): boolean {
  const mode = useAtomValue(sectionModeAtom);
  const [zoomedOut, setZoomedOut] = useState(false);

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || mode !== 'well_to_well') {
      return;
    }
    map.triggerRepaint();
  }, [mapRef, mode]);

  // Followed after that too: the reader can zoom back out past where the wells draw.
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || mode !== 'well_to_well') {
      return undefined;
    }
    /** Whether the map is out past where the wells draw. */
    const check = () => setZoomedOut(map.getZoom() < ELIGIBLE_MIN_ZOOM);
    check();
    map.on('zoomend', check);
    return () => {
      map.off('zoomend', check);
    };
  }, [mapRef, mode]);

  return mode === 'well_to_well' && zoomedOut;
}

/** Map Query's datasets that Area Scoped Datasets shows, in tab order. */
export const AREA_DATASET_IDS = ['wells', 'documents', 'subsurfaceMaps'];

type JotaiStore = ReturnType<typeof createStore>;
type AreaPolygon = NonNullable<DatasetSearchParams['polygon_geojson']>;

// What a reader set on a table, kept when the scope changes under it.
const KEPT_KEYS = ['text', 'sortAttribute', 'sortDirection', 'pageSize'];

/**
 * A dataset's search inside the area: its Map Query default payload with the area's
 * boundary as the AOI polygon, the rail's well filters on Wells, and the reader's
 * own search and sort carried over, back on page 1.
 *
 * @param {string} id - The Map Query dataset.
 * @param {AreaPolygon} polygon - The area's boundary.
 * @param {object} wellParams - The rail's well filters, as wells query params.
 * @param {DatasetSearchParams | undefined} current - The search it replaces.
 * @return {DatasetSearchParams} The scoped search.
 */
const scopedSearch = (
  id: string,
  polygon: AreaPolygon,
  wellParams: Record<string, string[]>,
  current: DatasetSearchParams | undefined
): DatasetSearchParams => {
  const previous = (current ?? {}) as Record<string, unknown>;
  const kept = Object.fromEntries(
    KEPT_KEYS.filter((key) => previous[key] !== undefined).map((key) => [
      key,
      previous[key],
    ])
  );
  return {
    ...datasetRegistry[id].defaultPayload,
    ...kept,
    ...(id === 'wells' ? wellParams : {}),
    polygon_geojson: polygon,
    page: 1,
  };
};

/**
 * Area Scoped Datasets runs Map Query's own dataset tables, so it needs Map Query's
 * state; this is a store of its own for that, so nothing here touches Map Query's,
 * and a polygon area bounds every search in it. A search starts unbounded the
 * moment its atom is first read, so the store is subscribed and scoped in the same
 * tick, which aborts that first request at once. Map Query's map ref is
 * the page's map, so "Show on map" moves it, and a well picked there is the one the
 * page's halo marks.
 *
 * @param {Geometry | null | undefined} geometry - The area's boundary.
 * @param {object} wellParams - The rail's well filters, as wells query params;
 * memoised, since a new object re-scopes every search.
 * @param {MapRef | null} mapRef - The page's map.
 * @return {JotaiStore} The store, for the tables and the map layers alike.
 */
export const useAreaDatasetStore = (
  geometry: Geometry | null | undefined,
  wellParams: Record<string, string[]>,
  mapRef: MapRef | null
): JotaiStore => {
  const [store] = useState(() => {
    const areaStore = createStore();
    // In AOI mode the lists search on mount instead of waiting for map bounds,
    // which nothing here sends.
    areaStore.set(spatialFilterModeAtom, 'aoi-polygon');
    return areaStore;
  });
  const setSelectedUwi = useSetAtom(selectedWellUwiAtom);
  const polygon =
    geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon'
      ? geometry
      : null;

  useEffect(() => {
    if (!polygon) return undefined;
    const unsubscribe = AREA_DATASET_IDS.map((id) =>
      store.sub(allDatasetAtoms[id].searchAtom, () => {})
    );
    AREA_DATASET_IDS.forEach((id) => {
      const atoms = allDatasetAtoms[id];
      store.set(atoms.searchAtom, (current) =>
        scopedSearch(id, polygon, wellParams, current)
      );
      store.set(atoms.highlightedItemIdAtom, null);
      store.set(atoms.highlightedItemBoundsAtom, null);
    });
    // The Workspaces view reads its polygon from here, as Map Query's does.
    store.set(lastSpatialFilterAtom, { polygon_geojson: polygon });
    return () => unsubscribe.forEach((unsub) => unsub());
  }, [store, polygon, wellParams]);

  useEffect(() => {
    store.set(mapSearchRefAtom, mapRef);
  }, [store, mapRef]);

  useEffect(() => {
    const wellHighlight = allDatasetAtoms.wells.highlightedItemIdAtom;
    return store.sub(wellHighlight, () =>
      setSelectedUwi(store.get(wellHighlight))
    );
  }, [store, setSelectedUwi]);

  return store;
};
